/* Виджет CloudPayments (PAY-8): скрипт грузится только когда тренер нажал «Оплатить» — страница от него не зависит.
   Параметры платежа (сумма, счёт, чек) приходят с сервера (trainer.payStart). */
import { trpc } from '@tg/app-core';

const SRC = 'https://widget.cloudpayments.ru/bundles/cloudpayments.js';

interface CpWidget {
  pay(method: 'charge' | 'auth', opts: Record<string, unknown>, cb: { onSuccess?: () => void; onFail?: (reason: string) => void; onComplete?: () => void }): void;
}
declare global {
  interface Window {
    cp?: { CloudPayments: new (o?: Record<string, unknown>) => CpWidget };
  }
}

let loading: Promise<void> | null = null;
function load() {
  if (window.cp) return Promise.resolve();
  if (!loading)
    loading = new Promise<void>((ok, fail) => {
      const s = document.createElement('script');
      s.src = SRC;
      s.async = true;
      s.onload = () => ok();
      s.onerror = () => {
        loading = null;
        s.remove();
        fail(new Error('Не загрузилась форма оплаты — проверьте интернет'));
      };
      document.head.appendChild(s);
    });
  return loading;
}

export type Widget = { method: 'charge' | 'auth'; invoiceId: string } & Record<string, unknown>;

/** Открыть форму оплаты; ok — платёж прошёл, cancel — закрыли или отказ банка (причина — в reason) */
export async function payWidget(w: Widget): Promise<{ ok: boolean; reason?: string }> {
  await load();
  const { method, ...opts } = w;
  return new Promise((done) => {
    new window.cp!.CloudPayments({ language: 'ru-RU' }).pay(method, { ...opts, skin: 'mini', requireEmail: false }, {
      onSuccess: () => done({ ok: true }),
      onFail: (reason) => done({ ok: false, reason }),
    });
  });
}

/** Оплата целиком: payStart → (сохранённая карта | виджет) → проверка платежа → обновить данные кабинета */
export function usePay() {
  const utils = trpc.useUtils();
  const start = trpc.trainer.payStart.useMutation();
  const check = trpc.trainer.payCheck.useMutation();
  const refresh = () => Promise.all([utils.trainer.me.invalidate(), utils.trainer.payments.invalidate()]);
  async function pay(input: Parameters<typeof start.mutateAsync>[0]): Promise<{ ok: boolean; msg?: string }> {
    try {
      const r = await start.mutateAsync(input);
      if (r.done) {
        await refresh();
        return { ok: true };
      }
      const w = await payWidget(r.widget as Widget);
      if (!w.ok) return { ok: false, msg: w.reason && !/cancel/i.test(w.reason) ? 'Оплата не прошла: ' + w.reason : undefined };
      /* Уведомление CloudPayments обычно приходит за секунды — проверяем несколько раз */
      for (let k = 0; k < 5; k++) {
        const c = await check.mutateAsync({ id: r.widget.invoiceId });
        if (c.status === 'paid' || c.status === 'card') break;
        await new Promise((ok) => setTimeout(ok, 1500));
      }
      await refresh();
      return { ok: true };
    } catch (e) {
      return { ok: false, msg: e instanceof Error ? e.message : 'Не получилось' };
    }
  }
  return { pay, busy: start.isPending || check.isPending };
}
